import { type PropsWithChildren, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { getGetMyProfileQueryKey, useEditQuizUser, useGetMyProfile } from '@/api';
import { EnterTracking } from '@/components/EnterTracking';
import { usePreventNavigation } from '@/hooks/usePreventNavigation';
import { CheckSmallCircleFillIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { ApiTokenStorage } from '@/module/auth';
import { type UserType } from '@/types';
import { alertDialog, confirmDialog } from '@/ui_v3';
import { getSchemaByUserType } from './schema/utils';
import { useFormDefaultValues } from './hooks';
import { MarketingAgreementSection } from './MarketingAgreementSection';
import { ProfileEditActions } from './ProfileEditActions';
import { ProfileEditForm } from './ProfileEditForm';
import { transformProfileFormToApiRequest } from './profileFormToApiTransformer';
import { type ProfileEditFormData } from './schema';
const ProfileEditLayout = ({
  children
}: PropsWithChildren) => {
  return <div className="flex flex-col gap-lg" data-sentry-component="ProfileEditLayout" data-sentry-source-file="ProfileEditContainer.tsx">{children}</div>;
};
export const ProfileEditContainer = () => {
  const {
    t
  } = useTranslation(['common']);
  const queryClient = useQueryClient();
  const {
    syncLatestUserInfo
  } = useUser();
  const {
    data: userProfile,
    isFetching
  } = useGetMyProfile({
    query: {
      staleTime: 0,
      refetchOnMount: 'always'
    }
  });
  const schema = getSchemaByUserType(userProfile?.data.type.toLowerCase() as UserType);
  const formDefaultValues = useFormDefaultValues();
  const {
    mutate: editQuizUser,
    isPending
  } = useEditQuizUser({
    mutation: {
      onSuccess: async () => {
        const currentValues = form.getValues();
        logger.track('enter_profile_edit_save', {
          user_type: currentValues.userType,
          country_code: currentValues.countryCode,
          username: currentValues.userName,
          school_name: 'schoolName' in currentValues ? currentValues.schoolName : null,
          school_type: 'schoolType' in currentValues ? currentValues.schoolType : null,
          school_grade: 'schoolGrade' in currentValues ? currentValues.schoolGrade : null,
          phone_number: currentValues.phoneNumber || null,
          company_name: 'companyName' in currentValues ? currentValues.companyName : null,
          position: 'position' in currentValues ? currentValues.position : null,
          marketing_email: currentValues.emailMarketingAgreement,
          marketing_phone: currentValues.phoneMarketingAgreement
        });

        // 프로필 캐시 무효화 및 리페치 - 다른 페이지에서도 최신 데이터를 볼 수 있도록
        void queryClient.invalidateQueries({
          queryKey: getGetMyProfileQueryKey()
        });
        void syncLatestUserInfo(ApiTokenStorage.get());
        toast.success(t('profileEdit.saveSuccess'), {
          position: 'bottom-left',
          className: 'text-body-lg-semibold',
          icon: <CheckSmallCircleFillIcon size="md" color={ICON_COLOR.positive} />
        });

        // 저장 성공 시 폼은 수정된 값으로 리셋하여 isDirty 상태 초기화
        form.reset(currentValues);
      },
      onError: error => {
        logger.captureError(error);
        alertDialog.open({
          header: {
            title: t('profileEdit.saveError')
          },
          confirmText: t('global.confirm.button')
        });
      }
    }
  });
  const form = useForm<ProfileEditFormData>({
    resolver: zodResolver(schema),
    defaultValues: formDefaultValues,
    mode: 'onChange'
  });
  const isDirty = form.formState.isDirty;

  // 페이지 이동 방지
  usePreventNavigation({
    when: isDirty,
    onNavigationAttempt: resumeNavigation => {
      void (async () => {
        const confirmed = await confirmDialog.openAsync({
          header: {
            title: t('profileEdit.exitWithoutSaving')
          },
          body: t('profileEdit.exitWithoutSaving.body'),
          confirmText: t('profileEdit.exitWithoutSaving.confirmText')
        });
        if (confirmed) {
          resumeNavigation();
        }
      })();
    }
  });
  const handleSubmit = form.handleSubmit(async () => {
    const data = form.getValues();
    const userType = data.userType as UserType;
    const requestData = transformProfileFormToApiRequest(data, userType);
    editQuizUser({
      data: requestData
    });
  });
  useEffect(() => {
    // 사용자가 수정 중(dirty)일 때 외부에서 프로필 쿼리가 갱신되면(예: 아바타 모달
    // 저장 후 invalidate) form.reset 이 저장 안 한 입력값을 서버 값으로 덮어쓴다.
    // dirty 가 아닐 때만 기본값으로 리셋한다(초기 로드/저장 후 리셋은 그대로 동작).
    if (formDefaultValues && !form.formState.isDirty) {
      form.reset(formDefaultValues);
    }
  }, [formDefaultValues, form]);
  if (!formDefaultValues) {
    return null;
  }
  return <EnterTracking eventName="enter_profile_edit" eventProperties={{
    has_verified_phone_number: Boolean(userProfile?.data.verifiedPhoneNumber)
  }} isReady={Boolean(userProfile) && !isFetching} data-sentry-element="EnterTracking" data-sentry-component="ProfileEditContainer" data-sentry-source-file="ProfileEditContainer.tsx">
      <FormProvider {...form} data-sentry-element="FormProvider" data-sentry-source-file="ProfileEditContainer.tsx">
        <ProfileEditLayout data-sentry-element="ProfileEditLayout" data-sentry-source-file="ProfileEditContainer.tsx">
          <ProfileEditForm data-sentry-element="ProfileEditForm" data-sentry-source-file="ProfileEditContainer.tsx" />
          <MarketingAgreementSection data-sentry-element="MarketingAgreementSection" data-sentry-source-file="ProfileEditContainer.tsx" />
          <ProfileEditActions onSubmit={handleSubmit} isPending={isPending} isDirty={isDirty} data-sentry-element="ProfileEditActions" data-sentry-source-file="ProfileEditContainer.tsx" />
        </ProfileEditLayout>
      </FormProvider>
    </EnterTracking>;
};